/**
 * Property Shell — Barba.js integration with persistent hero video
 * =============================================================================
 * Handles SPA-style navigation for property pages while keeping the hero video
 * persistent across transitions.
 * =============================================================================
 */

(function () {
  'use strict';

  // Only initialize if Barba is available and we're in a property shell context
  if (typeof barba === 'undefined') return;

  const SHELL_VIDEO_TIME_KEY = 'shell-video-time';
  const SHELL_VIDEO_PROP_KEY = 'shell-video-property-id';

  /**
   * Get the persistent video element (inside the video_tag wrapper)
   */
  function getHeroVideo() {
    const wrapper = document.getElementById('property-hero-video-wrapper');
    return wrapper ? wrapper.querySelector('video') : null;
  }

  /**
   * Get the hero shell container
   */
  function getHeroShell() {
    return document.getElementById('property-hero-shell');
  }

  /**
   * Get the micro bar element
   */
  function getMicroBar() {
    return document.getElementById('property-micro-bar');
  }

  /**
   * Update micro-bar background on scroll
   */
  function initMicroBarScroll() {
    const microBar = getMicroBar();
    const heroShell = getHeroShell();
    if (!microBar || !heroShell) return;

    const heroBottom = heroShell.offsetTop + heroShell.offsetHeight;

    function onScroll() {
      const scrollY = window.scrollY || window.pageYOffset;
      if (scrollY > heroBottom - 48) {
        microBar.classList.add('property-micro-bar--scrolled');
      } else {
        microBar.classList.remove('property-micro-bar--scrolled');
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Initial check
  }

  /**
   * Crossfade the hero video when the property changes
   */
  function crossfadeHeroVideo(nextContainer) {
    const heroShell = getHeroShell();
    const video = getHeroVideo();
    if (!heroShell) return Promise.resolve();

    const nextPropertyId = nextContainer.dataset.propertyId;
    const nextVideoUrl = nextContainer.dataset.heroVideoUrl;
    const currentPropertyId = heroShell.dataset.propertyId;

    // Same property — just restore playback position
    if (nextPropertyId && nextPropertyId === currentPropertyId) {
      if (video) {
        const savedTime = parseFloat(sessionStorage.getItem(SHELL_VIDEO_TIME_KEY)) || 0;
        video.currentTime = savedTime;
        video.play().catch(() => {});
      }
      return Promise.resolve();
    }

    // Different property — crossfade video source
    return new Promise((resolve) => {
      heroShell.style.transition = 'opacity 300ms ease';
      heroShell.style.opacity = '0.3';

      setTimeout(() => {
        // Update property ID on shell
        if (nextPropertyId) {
          heroShell.dataset.propertyId = nextPropertyId;
        }

        // If we have a new video URL, replace the entire video element
        // (video_tag output can't be modified by swapping sources)
        const wrapper = document.getElementById('property-hero-video-wrapper');
        if (nextVideoUrl && wrapper) {
          const wasPlaying = video ? !video.paused : true;

          // Build a fresh video element matching video_tag output format
          const newVideo = document.createElement('video');
          newVideo.setAttribute('playsinline', 'playsinline');
          newVideo.setAttribute('autoplay', 'autoplay');
          newVideo.setAttribute('loop', 'loop');
          newVideo.setAttribute('muted', 'muted');
          newVideo.setAttribute('preload', 'metadata');
          newVideo.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;border:none;';

          const source = document.createElement('source');
          source.src = nextVideoUrl;
          source.type = 'video/mp4';
          newVideo.appendChild(source);

          // Replace old video
          if (video) {
            wrapper.replaceChild(newVideo, video);
          } else {
            wrapper.appendChild(newVideo);
          }

          newVideo.currentTime = 0;
          if (wasPlaying) {
            newVideo.play().catch(() => {});
          }
        } else if (!nextVideoUrl && wrapper && video) {
          // No video on next property — remove the video element
          video.pause();
          wrapper.removeChild(video);
        }

        heroShell.style.opacity = '1';
        setTimeout(resolve, 300);
      }, 300);
    });
  }

  /**
   * Re-initialize Shopify sections after Barba transition
   */
  function reinitShopifySections(container) {
    // Re-run any Shopify section scripts that bind on DOMContentLoaded
    if (window.Shopify && window.Shopify.designMode) return;

    // Dispatch custom event for other scripts to hook into
    window.dispatchEvent(new Event('plekify:page-changed'));

    // Trigger Shopify's own section load events if available
    if (window.__SHOPIFY_EVENT_BUS__) {
      window.__SHOPIFY_EVENT_BUS__.emit('section:load');
    }

    // Re-initialize any lazy-loaded images
    if (window.lazySizes) {
      window.lazySizes.init();
    }
  }

  /**
   * Update micro-bar scroll listener after transition
   */
  function reinitMicroBar() {
    const microBar = getMicroBar();
    if (microBar) {
      microBar.classList.remove('property-micro-bar--scrolled');
    }
    initMicroBarScroll();
  }

  // ========================================================================
  // Barba Hooks
  // ========================================================================

  barba.hooks.before(() => {
    barba.wrapper.classList.add('is-animating');

    // Save current video playback time
    const video = getHeroVideo();
    if (video) {
      sessionStorage.setItem(SHELL_VIDEO_TIME_KEY, String(video.currentTime));
      const heroShell = getHeroShell();
      if (heroShell) {
        sessionStorage.setItem(SHELL_VIDEO_PROP_KEY, heroShell.dataset.propertyId || '');
      }
    }
  });

  barba.hooks.after(() => {
    barba.wrapper.classList.remove('is-animating');
  });

  // ========================================================================
  // Barba Init
  // ========================================================================

  barba.init({
    debug: false,
    prefetch: true,
    timeout: 5000,
    prevent: ({ el }) => {
      // Don't Barba-navigate external links, anchor links, or modifier+click
      const href = el.getAttribute('href');
      if (href && href.startsWith('#')) return true;
      if (el.getAttribute('target') === '_blank') return true;
      if (el.closest('[data-no-barba]')) return true;
      // Don't navigate if it's not a property shell link
      // Allow Barba to handle all internal links within the shell wrapper
      return false;
    },
    transitions: [
      {
        name: 'property-fade',
        sync: true, // IMPORTANT: allows URL to update immediately
        leave(data) {
          // Use CSS transition for leave
          const container = data.current.container;
          container.style.transition = 'opacity 150ms ease';
          container.style.opacity = '0';
          return new Promise((resolve) => setTimeout(resolve, 150));
        },
        enter(data) {
          const container = data.next.container;
          container.style.opacity = '0';
          container.style.transition = 'opacity 150ms ease';

          // Force reflow
          void container.offsetWidth;

          container.style.opacity = '1';
          return new Promise((resolve) => setTimeout(resolve, 150));
        },
        after(data) {
          const currentProp = data.current.container.dataset.propertyId;
          const nextProp = data.next.container.dataset.propertyId;

          // Crossfade hero video if property changed
          crossfadeHeroVideo(data.next.container).then(() => {
            // Re-init Shopify sections and scripts
            reinitShopifySections(data.next.container);

            // Re-init micro bar scroll detection
            reinitMicroBar();

            // Scroll to top on new property, restore position on same-property tab
            if (currentProp !== nextProp) {
              window.scrollTo(0, 0);
            }

            // Update document title
            const newTitle = data.next.html.match(/<title>(.*?)<\/title>/i)?.[1];
            if (newTitle) document.title = newTitle;
          });
        },
      },
    ],
  });

  // ========================================================================
  // Initial setup
  // ========================================================================

  document.addEventListener('DOMContentLoaded', () => {
    initMicroBarScroll();
  });
})();
